<template>
  <SfModal v-if="showModal" :hide-close="true" :scrollable-content="false">
    <template #title>
      {{ I18n.t('accounts.locking.modal.v2.title') }}
    </template>

    <template #body>
      <div>
        <div class="sf-align-c sf-row sf-row-center sf-row-middle">
          <div>
            <div class="sf-pad-b-1">
              {{ I18n.t('accounts.locking.modal.v2.lock_up_to_date') }}
            </div>
            <div class="sf-pad-b-1">
              <SfDropdown
                :variant="dropdownVariant"
                :dropdown-id="currentDropdownId"
                :is-open="isScopedDropdownOpen"
                @dropdown-clicked="handleToggleDropdown"
                @dropdown-closed="handleCloseDropdown"
              >
                <template #trigger>
                  <div class="accounts-locking__dropdown--item">
                    {{ selectedYearEnd.label }}
                  </div>
                </template>

                <template #menu>
                  <div
                    class="tw-overflow-y-auto accounts-locking__dropdownlist"
                  >
                    <SfDropdownItem @click.stop="handleClickNewPeriod()">
                      {{ I18n.t('accounts.locking.modal.v2.new_period') }}
                    </SfDropdownItem>
                    <SfDropdownItem
                      v-for="option in yearsToSelect"
                      :key="option.year_end"
                      :is-disabled="option.all_periods_locked_for_fiscal_year"
                      :is-wrapped="false"
                      @click.stop="handleItemClick(option)"
                    >
                      <div class="align-center">
                        {{ option.label }}
                        <span
                          v-if="option.all_periods_locked_for_fiscal_year"
                          class="sf-pad-l-2 fa fa-lock"
                        />
                      </div>
                    </SfDropdownItem>
                  </div>
                </template>
              </SfDropdown>
            </div>

            <div
              class="
                accounts-locking__description
                sf-pad-b-1 sf-small sf-font-xs
              "
            >
              {{ I18n.t('accounts.locking.modal.v2.description') }}
            </div>
          </div>
        </div>
      </div>
    </template>

    <template #footer>
      <div class="accounts-locking__footer">
        <button class="sf-btn sf-btn-success" @click.stop="optIn">
          <div v-if="saving" class="fa fa-spinner fa-spin" />
          {{ I18n.t('accounts.locking.modal.v2.save') }}
        </button>
      </div>
    </template>
  </SfModal>
</template>

<script type="text/javascript">
import { dropdowns } from '@app/shared/types/dropdown'
import { variants } from '@app/shared/vue/components/sf-dropdown/constants'
import { useDropdown } from '@app/shared/pinia/dropdown'
import { mapActions, mapState } from 'pinia'

import { SfModal } from '@app/shared/vue/components/sf-modal'
import SFetch from '@app/utils/sfetch'
import SfDropdown from '@app/shared/vue/components/sf-dropdown/sf-dropdown.vue'
import SfDropdownItem from '@app/shared/vue/components/sf-dropdown/sf-dropdown-item.vue'

export default {
  name: 'AccountsLockingModal',
  components: { SfDropdownItem, SfDropdown, SfModal },
  props: {
    yearsToSelectJson: {
      type: Object,
      default: () => ({}),
    },
    optInUrl: {
      type: String,
      required: true,
    },
    triggerOnMappingListRemoval: {
      type: Boolean,
      default: false,
    },
    mappingListId: {
      type: String,
      default: null,
    },
    initiallyHideModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModal: !this.initiallyHideModal,
      yearsToSelect: this.parseYears(),
      selectedYearEnd: {
        year_end: null,
        label: '-',
        all_periods_locked_for_fiscal_year: false,
      },
      dropdownVariant: variants.neutral,
      saving: false,
    }
  },
  computed: {
    ...mapState(useDropdown, ['currentDropdownId']),
    isScopedDropdownOpen() {
      return this.currentDropdownId === dropdowns.LOCK_ACCOUNTS_MODAL
    },
    isShownOnConfigurationScreen() {
      return this.triggerOnMappingListRemoval === true
    },
    // We show the select only when we have at least 1 mapping list to select + the empty option
    // and we are not showing this at all on the company edit page
  },
  created() {
    this.dropdownId = dropdowns.LOCK_ACCOUNTS_MODAL
  },
  mounted() {
    // Track changes for company configuration. We care about
    // the case when the mapping list is completely removed
    //
    if (this.isShownOnConfigurationScreen) {
      const select = document.getElementById('company_account_mapping_list_id')
      const originalValue = select.value

      select.addEventListener('change', (e) => {
        const newValue = e.currentTarget.value

        if (originalValue !== newValue && newValue === '') {
          this.showModal = true
        }
      })
    }
  },
  methods: {
    ...mapActions(useDropdown, ['toggleDropdown', 'closeDropdown']),
    handleCloseDropdown() {
      this.closeDropdown(dropdowns.LOCK_ACCOUNTS_MODAL)
    },
    handleToggleDropdown() {
      this.toggleDropdown(dropdowns.LOCK_ACCOUNTS_MODAL)
    },
    handleClickNewPeriod() {
      this.selectedYearEnd = {
        year_end: null,
        newPeriod: true,
        label: I18n.t('accounts.locking.modal.v2.new_period'),
        all_periods_locked_for_fiscal_year: false,
      }
      this.dropdownVariant = variants.neutral
      this.handleCloseDropdown()
    },
    handleItemClick(option) {
      this.selectedYearEnd = option
      this.selectedYearEnd.newPeriod = false
      this.dropdownVariant = variants.neutral
      this.handleCloseDropdown()
    },
    async optIn() {
      if (
        this.selectedYearEnd.year_end === null &&
        !this.selectedYearEnd.newPeriod
      ) {
        this.dropdownVariant = variants.danger

        return
      }

      if (this.saving) {
        return
      }

      try {
        this.saving = true

        await SFetch.json(this.optInUrl, {
          method: 'POST',
          body: {
            new_period: this.selectedYearEnd.newPeriod,
            lock_up_to_date: this.selectedYearEnd.previous_year_end,
            snapshot_current_aml: this.isShownOnConfigurationScreen,
          },
        })
        this.showModal = false
        this.saving = false
      } catch {
        this.saving = false
        // Silently fail :(
      }
    },
    parseYears() {
      return this.yearsToSelectJson
    },
  },
}
</script>

<style scoped lang="scss">
@import 'silverfin-ui-kit/src/scss/tooling';

.accounts-locking {
  &__description {
    margin: 0 auto;
  }

  &__dropdown--item {
    min-width: 160px;
  }

  &__dropdownlist {
    max-height: calc(100vh - 300px);
  }

  &__footer {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: space(2);

    &--horizontal {
      flex-direction: row;
      justify-content: space-between;
    }
  }
}
</style>
